import type { RequestDetail } from "./api";

/** Input classes, stacked bottom to top in the palette's validated order (slots 1-3). */
export const INPUT_CLASSES = [
  { key: "cacheRead", label: "Read from cache", color: "var(--kp-series-1)" },
  { key: "cacheWrite", label: "Written to cache", color: "var(--kp-series-2)" },
  { key: "text", label: "New input", color: "var(--kp-series-3)" },
] as const;
export type InputClass = (typeof INPUT_CLASSES)[number]["key"];

/** Model identity on the strip under the bars: three validated hues, then "other". */
export const MODEL_COLORS = ["var(--kp-series-5)", "var(--kp-series-6)", "var(--kp-series-7)"];
export const OTHER_MODEL = "var(--kp-other)";

/** A gap between requests longer than this is marked on the timeline (the short cache lifetime). */
export const IDLE_MARK_MS = 5 * 60_000;

export const inputTokens = (r: RequestDetail) => r.tokens.text + r.tokens.cacheRead + r.tokens.cacheWrite;
export const billedCost = (r: RequestDetail) => (r.cacheHit ? 0 : r.cost.total);

export interface Segment { cls: InputClass; y: number; h: number }
export interface BarGeometry {
  x: number;
  width: number;
  /** Hit target: the whole band, taller than the mark. */
  bandX: number;
  bandWidth: number;
  segments: Segment[];
  outputH: number;
  costH: number;
}
export interface Layout {
  bars: BarGeometry[];
  gaps: Array<{ x: number; ms: number; index: number }>;
  maxInput: number;
  maxOutput: number;
  maxCost: number;
  modelColor: Map<string, string>;
}

/** Surface gap between stacked segments and between touching bars (marks-and-anatomy). */
const GAP = 2;
const MAX_BAR = 24;

/**
 * Bars in request order, one band each. x is request order, not clock time: a 500-request session
 * stays readable, and idle time is shown by gap markers instead of empty space.
 */
export function layout(rows: RequestDetail[], width: number, heights: { input: number; output: number; cost: number }): Layout {
  const n = Math.max(1, rows.length);
  const band = width / n;
  // Leave a surface gap between bars when there is room for one; very dense sessions touch.
  const barW = Math.max(1, Math.min(MAX_BAR, band >= 4 ? band - GAP : band));
  const maxInput = Math.max(1, ...rows.map(inputTokens));
  const maxOutput = Math.max(1, ...rows.map((r) => r.tokens.output));
  const maxCost = Math.max(1e-9, ...rows.map(billedCost));

  const counts = new Map<string, number>();
  for (const r of rows) counts.set(r.model, (counts.get(r.model) ?? 0) + 1);
  const ranked = [...counts].sort((a, b) => b[1] - a[1]).map(([m]) => m);
  // Colour follows the model, by first appearance among the top three; the rest are "other".
  const top = new Set(ranked.slice(0, MODEL_COLORS.length));
  const order = [...new Set(rows.map((r) => r.model))].filter((m) => top.has(m));
  const modelColor = new Map<string, string>(ranked.map((m) => [m, OTHER_MODEL]));
  order.forEach((m, i) => modelColor.set(m, MODEL_COLORS[i]!));

  const bars = rows.map((r, i) => {
    const bandX = i * band;
    const x = bandX + (band - barW) / 2;
    const total = inputTokens(r);
    const totalH = (total / maxInput) * heights.input;
    let y = heights.input;
    const segments: Segment[] = [];
    for (const c of INPUT_CLASSES) {
      const v = r.tokens[c.key];
      if (v <= 0) continue;
      const h = (v / total) * totalH;
      // A gap only between segments that are tall enough to keep one; tiny slivers stay visible.
      const gap = segments.length > 0 && h > GAP * 2 ? GAP : 0;
      segments.push({ cls: c.key, y: y - h + gap, h: Math.max(h - gap, total > 0 ? 0.5 : 0) });
      y -= h;
    }
    return {
      x, width: barW, bandX, bandWidth: band, segments,
      outputH: (r.tokens.output / maxOutput) * heights.output,
      costH: (billedCost(r) / maxCost) * heights.cost,
    };
  });

  const gaps = [];
  for (let i = 1; i < rows.length; i++) {
    const ms = Date.parse(rows[i]!.startTime) - Date.parse(rows[i - 1]!.startTime);
    if (ms > IDLE_MARK_MS) gaps.push({ x: i * band, ms, index: i });
  }
  return { bars, gaps, maxInput, maxOutput, maxCost, modelColor };
}

/**
 * Which gaps get a text label: longest first, each at least `room` px from every other label and
 * from the right edge. The rest keep their marker line only, so labels never collide.
 */
export function labelledGaps(gaps: Layout["gaps"], width: number, room = 84): Set<number> {
  const placed: number[] = [];
  const out = new Set<number>();
  for (const g of [...gaps].sort((a, b) => b.ms - a.ms)) {
    if (g.x + room > width) continue;
    if (placed.some((x) => Math.abs(x - g.x) < room)) continue;
    placed.push(g.x);
    out.add(g.index);
  }
  return out;
}

/** An SVG path for a bar with 4px rounded top corners and a square base. */
export function roundedTop(x: number, y: number, w: number, h: number, r = 4) {
  const rr = Math.max(0, Math.min(r, w / 2, h));
  return `M${x},${y + h}V${y + rr}Q${x},${y} ${x + rr},${y}H${x + w - rr}Q${x + w},${y} ${x + w},${y + rr}V${y + h}Z`;
}

/** Summary figures for the tiles above the chart. */
export function summarise(rows: RequestDetail[]) {
  const input = rows.reduce((a, r) => a + inputTokens(r), 0);
  const read = rows.reduce((a, r) => a + r.tokens.cacheRead, 0);
  const largest = rows.reduce((a, r) => Math.max(a, inputTokens(r)), 0);
  const slowest = rows.reduce((a, r) => Math.max(a, r.durationMs ?? 0), 0);
  const first = rows[0]?.startTime;
  const last = rows[rows.length - 1]?.startTime;
  return {
    cost: rows.reduce((a, r) => a + billedCost(r), 0),
    cacheShare: input > 0 ? read / input : null,
    largestContext: largest,
    slowestMs: slowest,
    spanMs: first && last ? Date.parse(last) - Date.parse(first) : 0,
    failed: rows.filter((r) => r.status !== "success").length,
  };
}

export function duration(ms: number) {
  if (ms < 60_000) return `${Math.round(ms / 1000)} s`;
  const m = Math.round(ms / 60_000);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
}
